/**
 * Guest → register hand-off for "Сохранить поиск".
 *
 * A signed-out user who picks a category/filter and clicks Save has their
 * intended search stashed in localStorage, then gets sent to /register. After
 * they authenticate, `PendingSavedSearchSync` reads this stash, creates the
 * saved search for real, and clears it (see pending-saved-search-sync.tsx).
 *
 * All access is guarded for SSR / private-mode (no window, or storage throwing).
 */

import { useSyncExternalStore } from 'react';

export const PENDING_SAVED_SEARCH_KEY = 'uztender_pending_saved_search';

/** Filter snapshot to replay as a saved search once the user is authed. Field
 *  names mirror {@link CreateSavedSearchInput} so it maps straight through. */
export interface PendingSavedSearch {
  name?: string;
  keyword?: string;
  regionCode?: string;
  categorySlug?: string;
  ikpuCode?: string;
  /** Slug of the niche (Spec #3 classification layer) the guest picked, e.g.
   *  from the niche search-as-you-type. Mirrors `ikpuCode` alongside it. */
  nicheSlug?: string;
  /** Fixed catalog layer the guest was browsing (e.g. 'ifi' on /ifi) when
   *  they clicked Save. /ifi is a public, indexable page a signed-out
   *  visitor can browse, so dropping this here would leave the "wrong
   *  layer" defect alive for exactly the users most likely to hit it. */
  scope?: string;
  /** Set when the guest was browsing `/supply` (`scope === 'supply'`) — see
   *  `SaveSearchButton`. A search born on that section must opt IN to
   *  supply matching or it silently matches nothing once created, and the
   *  guest hand-off would otherwise lose that the moment they registered. */
  includeSupply?: boolean;
  /** Budget floor / ceiling of the catalogue the guest was filtering (Spain,
   *  `features.guestSearchCarried`) — same units and meaning as the API's
   *  `minBudgetUzs` / `maxBudgetUzs`. Never written on UZ/KZ. */
  minBudgetUzs?: number;
  maxBudgetUzs?: number;
  /**
   * A guest saving the /results card — the reader's `results` search, not a
   * lots search. When set, the replay calls `PUT /me/results-search` with this
   * and ignores every lots field above.
   */
  /** `name` is a leftover of older stashes — ignored; the name is fixed. */
  results?: { criteria: object; name?: string };
}

export function stashPendingSavedSearch(input: PendingSavedSearch): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_SAVED_SEARCH_KEY, JSON.stringify(input));
  } catch {
    // Storage unavailable (private mode / quota) — silently skip; the guest just
    // lands on /register without the auto-save, which is an acceptable fallback.
  }
}

export function readPendingSavedSearch(): PendingSavedSearch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PENDING_SAVED_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSavedSearch;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingSavedSearch(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_SAVED_SEARCH_KEY);
  } catch {
    // no-op
  }
}

/*
 * The replay hand-off (Spain, `features.guestSearchCarried`).
 *
 * `PendingSavedSearchSync` creates the stashed search the moment the guest is
 * signed in, and the onboarding sheet decides what to ask in that same moment —
 * from a saved-search list that does not hold the search YET. The sheet used to
 * open on «¿Qué ofreces?» for a reader whose niche was being saved a second
 * later. So the sync says «a replay is under way» here, and the sheet waits
 * while there is a stash to replay or a replay in flight.
 */
let replayInFlight = false;
let releaseTimer: ReturnType<typeof setTimeout> | undefined;
const replayListeners = new Set<() => void>();

/** A replay that never answers must not hold the sheet forever. */
const REPLAY_MAX_HOLD_MS = 10_000;

export function setPendingReplayInFlight(value: boolean): void {
  if (releaseTimer !== undefined) {
    clearTimeout(releaseTimer);
    releaseTimer = undefined;
  }
  if (value) releaseTimer = setTimeout(() => setPendingReplayInFlight(false), REPLAY_MAX_HOLD_MS);
  if (replayInFlight === value) return;
  replayInFlight = value;
  replayListeners.forEach((listener) => listener());
}

function subscribeReplay(listener: () => void): () => void {
  replayListeners.add(listener);
  return () => void replayListeners.delete(listener);
}

function replaySnapshot(): boolean {
  return replayInFlight || readPendingSavedSearch() !== null;
}

/** True while a guest's stashed search is waiting for, or going through, its replay. */
export function usePendingSearchReplay(): boolean {
  return useSyncExternalStore(subscribeReplay, replaySnapshot, () => false);
}
